import { Maximize2Icon, Minimize2Icon } from 'lucide-react'

import {
  AddFilterButton,
  Button,
  Filters,
  Search,
  TableExport,
} from '@/components'
import type { Table } from '@/components/ui/table/types'
import type { Transaction } from '@/modules/projects'
import { transactionFilterSchema as schema } from '@/schemas/transactionFilterSchema'
import { useTableFilters } from '@/stores'
import {
  buildAccountNumberOptions,
  buildCountedOptions,
  COUNTED_FIELD_LABEL_FORMATTERS,
  isCountedField,
} from './utils'

interface TransactionsFiltersProps {
  table: Table<Transaction> | null
  isFullScreen: boolean
  setIsFullScreen: (value: boolean) => void
  hasShareNumber?: boolean
  dialogId: string
  exportFilename: string
}

export const TransactionsFilters = ({
  table,
  isFullScreen,
  setIsFullScreen,
  hasShareNumber = false,
  dialogId,
  exportFilename,
}: TransactionsFiltersProps) => {
  const filteredSchema = hasShareNumber
    ? schema
    : schema.filter((filter) => filter.field !== 'shareNumber')

  const { getUniqueValues } = useTableFilters<Transaction>({
    table,
    schema: filteredSchema,
  })

  const getUniqueValuesWithMetadata = (field: string) => {
    if (!table) return getUniqueValues(field)
    if (field === 'accountNumber') return buildAccountNumberOptions(table)
    if (isCountedField(field)) {
      return buildCountedOptions(
        table,
        field,
        COUNTED_FIELD_LABEL_FORMATTERS[field],
      )
    }
    return getUniqueValues(field)
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <Filters
        dialogId={dialogId}
        schema={filteredSchema}
        getUniqueValues={getUniqueValuesWithMetadata}
      />
      <div className="flex items-center gap-2">
        <span
          data-tour="transaction-filters"
          className="flex items-center gap-2"
        >
          <Search />
          <AddFilterButton dialogId={dialogId} schema={schema} />
        </span>
        {table && (
          <span data-tour="transaction-export" className="inline-flex">
            <TableExport table={table} filename={exportFilename} />
          </span>
        )}
        <Button
          theme="outline"
          size="sm"
          isIcon
          onClick={() => setIsFullScreen(!isFullScreen)}
        >
          {isFullScreen ? (
            <Minimize2Icon className="h-4 w-4" />
          ) : (
            <Maximize2Icon className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  )
}
